import { useEffect, useMemo } from 'react';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';

import type { Member as ImmutableMember, Member } from '#utils/members/records/Member';
import { createPendingChangesForm } from '#utils/pendingChanges/createPendingChangesForm';
import { SaveOptions } from '#utils/saveButtonUtils';
import type { Team as ImmutableTeam, Team } from '#utils/teams/records/Team';

import type { ReviewersMap } from '../FormSection/Approvals/SelectReviewerTag';
import type { PendingChangesFormProps } from '../pendingChangesFormTypes';

import { useReviewAndSaveContext } from './reviewAndSaveContext';
import {
  type FormRequirements,
  type PendingChangesFormSchema,
  getPendingChangesFormSchema,
  pendingChangesFormSchema,
} from './schema';

const isTeam = (reviewer: Member | Team): reviewer is Team => reviewer.constructor.name === 'Team';

export interface PendingChangesFormAdapterParams extends Pick<
  PendingChangesFormProps,
  'onEditPendingChangesForm' | 'pendingChangesFormState' | 'onSubmit' | 'saveOption'
> {
  reviewersMap: ReviewersMap;
  canBypassRequiredApprovals: boolean;
  minNumberOfApprovals: number;
}

/**
 * This adapter is used to convert the redux PendingChangesFormRecord state to a typed app form.
 * PendingChangesFormRecord uses immutable representations of members and teams, so we need it as a dependency.
 */
export const usePendingChangesFormAdapter = ({
  pendingChangesFormState: originalForm,
  onSubmit,
  reviewersMap,
  canBypassRequiredApprovals,
  minNumberOfApprovals,
  saveOption,
  onEditPendingChangesForm,
}: PendingChangesFormAdapterParams) => {
  const { isInitializing, setIsCustomScheduleSelected, setIsInitializing } = useReviewAndSaveContext();
  const pendingChangesFormState = originalForm.get('original');
  const pendingChangesFormStateModified = originalForm.get('modified');
  const isScheduleSelected = pendingChangesFormState.get('isScheduleSelected');
  const isUpdatingScheduledChange = !!pendingChangesFormState.get('executionDate');

  // isAcknowledgeConflictsRequired is async since it waits for some api calls to complete, or could change if scheduled change
  const isAcknowledgeConflictsRequired = pendingChangesFormStateModified.get('isAcknowledgeConflictsRequired');

  const isConfirmationRequired = pendingChangesFormState.get('isConfirmationRequired');
  const isCommentRequired = pendingChangesFormState.get('isCommentRequired');
  const usesLaunchDarklyApprovalService = pendingChangesFormState.get('usesLaunchDarklyApprovalService');
  const isBypassingApprovals = saveOption === SaveOptions.SAVE_CHANGES_BYPASSING_APPROVAL;
  const isApprovalRequired = isBypassingApprovals ? false : pendingChangesFormState.get('isApprovalSelected');

  const formRequirements: FormRequirements = useMemo(
    () => ({
      usesLaunchDarklyApprovalService,
      isConfirmationRequired,
      isAcknowledgeConflictsRequired,
      isCommentRequired,
      isApprovalRequired,
      canBypassRequiredApprovals: isApprovalRequired && canBypassRequiredApprovals,
      minNumberOfApprovals,
    }),

    [
      isConfirmationRequired,
      isAcknowledgeConflictsRequired,
      usesLaunchDarklyApprovalService,
      isCommentRequired,
      isApprovalRequired,
      canBypassRequiredApprovals,
      minNumberOfApprovals,
    ],
  );

  const resourceKey = pendingChangesFormState.get('resourceKey');
  const resourceName = pendingChangesFormState.get('resourceName');

  // this happens on initialization
  useEffect(() => {
    if (isInitializing) {
      onEditPendingChangesForm('isApprovalSelected', isApprovalRequired);
      setIsCustomScheduleSelected(isScheduleSelected);
      setIsInitializing(false);
    }
  }, [
    setIsCustomScheduleSelected,
    isScheduleSelected,
    isInitializing,
    isApprovalRequired,
    onEditPendingChangesForm,
    usesLaunchDarklyApprovalService,
    setIsInitializing,
  ]);

  useEffect(() => () => setIsInitializing(true), [setIsInitializing]);

  const zodSchema = useMemo(
    () =>
      getPendingChangesFormSchema({
        formRequirements,
        resourceInfo: { resourceKey, resourceName },
      }),
    [formRequirements, resourceKey, resourceName],
  );
  const reviewersArray: Array<{ type: 'team' | 'member'; id: string }> = [];
  // the reviewers array in PendingChangesFormRecord are expecting a javascript array, but createPendingChangesForm calls fromJS on props
  // so the javascript array is converted to an immutable array
  // this hack is to ensure that the reviewers array is a javascript array
  pendingChangesFormState.get('reviewers').forEach((reviewer) => {
    if (isTeam(reviewer)) {
      reviewersArray.push({ type: 'team', id: reviewer.key });
    } else {
      reviewersArray.push({ type: 'member', id: reviewer._id });
    }
  });

  const form = useTypedAppForm<PendingChangesFormSchema>({
    defaultValues: {
      executionDate: pendingChangesFormState.get('executionDate'),
      reviewers: reviewersArray,
      acknowledgeConflicts: pendingChangesFormState.get('acknowledgeConflicts'),
      resourceConfirmation: pendingChangesFormState.get('resourceConfirmation'),
      comment: pendingChangesFormState.get('comment') || pendingChangesFormState.get('description'),
      integrationConfig: pendingChangesFormState.get('integrationConfig'),
      bypassRequiredApprovals: isBypassingApprovals,
      // Fields that control presentation
      isScheduleSelected: pendingChangesFormState.get('isScheduleSelected'),
    },
    validators: {
      // NOTE: Do NOT add onMount validation here.
      // The form has conditional validation, onMount validation fails immediately because
      // these conditions aren't met on initial render.
      onChange: zodSchema,
      onSubmit: zodSchema,
    },

    listeners: {
      onChange: ({ fieldApi: field }) => {
        if (field.name === 'executionDate') {
          onEditPendingChangesForm('isScheduleSelected', (field.state.value ?? 0) > 0);
        }

        if (field.name === 'reviewers') {
          onEditPendingChangesForm('isApprovalSelected', isApprovalRequired || field.state.value.length > 0);
        }
      },
    },
    onSubmit: ({ value: { reviewers, bypassRequiredApprovals, ...rest } }) => {
      const members = reviewers
        .filter((reviewer) => reviewer.type === 'member')
        .map((reviewer) => reviewersMap.get(`member:${reviewer.id}`))
        .filter((member) => member !== undefined) as ImmutableMember[];
      const teams = reviewers
        .filter((reviewer) => reviewer.type === 'team')
        .map((reviewer) => reviewersMap.get(`team:${reviewer.id}`))
        .filter((team) => team !== undefined) as ImmutableTeam[];
      const reviewersList = [...members, ...teams];
      let formState = createPendingChangesForm({
        ...rest,
        isApprovalSelected:
          isBypassingApprovals || bypassRequiredApprovals ? false : isApprovalRequired || reviewersList.length > 0,
        reviewers: reviewersList,

        isScheduleSelected: !!rest.executionDate,
        description: rest.comment,
      });

      // There is a bug where JS lists are converted to immutable lists, so we need to handle them differently
      formState = formState.set('reviewers', reviewersList).set('notifyMembers', members).set('notifyTeams', teams);

      setIsCustomScheduleSelected(false);
      onSubmit(formState);
    },
  });

  // If the validation updates because of the approval selection, we need to update the form
  useEffect(() => {
    void form.validate('mount');
    void form.validate('change');
  }, [form, zodSchema]);

  return {
    isLoading: isInitializing,
    isUpdatingScheduledChange,
    form,
    formRequirements,
    resourceInfo: { resourceKey, resourceName },
  };
};

export type PendingChangesFormType = ReturnType<typeof usePendingChangesFormAdapter>['form'];
